import React, { useEffect } from 'react';
import Typography from "@mui/material/Typography";
import { useNavigate } from "react-router";
import { useStatus } from "../providers/MsgStatusProvider";
import { useLocation } from 'react-router-dom';
import { logError } from '../utils/logger';

function SocialConnectionThreads() {
    const navigate = useNavigate();
    const location = useLocation();
    const { setStatusMessage, setIsAlertOpen, setStatusType } = useStatus();

    useEffect(() => {
        // Get parameters from URL
        const searchParams = new URLSearchParams(location.search);
        const code = searchParams.get('code');
        const state = searchParams.get('state');
        const error = searchParams.get('error');
        const error_description = searchParams.get('error_description');
        const error_reason = searchParams.get('error_reason');

        console.log('Threads callback received with parameters:', {
            hasCode: !!code,
            hasState: !!state,
            error: error,
            errorDescription: error_description,
            errorReason: error_reason,
            fullUrl: location.search
        });

        // Handle errors from Threads authorization
        if (error) {
            let errorMessage = 'Failed to connect to Threads';
            
            if (error === 'access_denied') {
                errorMessage = 'Authorization was denied. Please try again and grant the necessary permissions.';
            } else if (error_description) {
                errorMessage = error_description;
            }
            
            logError('Threads authorization error', { 
                error, 
                error_description, 
                error_reason,
                url: location.search 
            });
            
            setStatusMessage(errorMessage);
            setIsAlertOpen(true);
            setStatusType('error');
            navigate('/profile');
            return;
        }

        // Validate required parameters
        if (!code) {
            logError('Threads callback missing code parameter', { url: location.search });
            setStatusMessage('Authorization failed: Missing authorization code. Please try again.');
            setIsAlertOpen(true);
            setStatusType('error');
            navigate('/profile');
            return;
        }

        if (!state) {
            logError('Threads callback missing state parameter', { url: location.search });
            setStatusMessage('Authorization failed: Missing security token. Please try again.');
            setIsAlertOpen(true);
            setStatusType('error');
            navigate('/profile');
            return;
        }

        // Process successful authorization with both code and state
        const url = `${process.env.REACT_APP_API_BASE_URL}connect/threads/callback/`;

        console.log('Sending Threads callback to backend:', {
            url: url,
            hasCode: !!code,
            hasState: !!state
        });

        fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Token ${localStorage.getItem('token')}`
            },
            body: JSON.stringify({ 
                code: code,
                state: state 
            })
        })
            .then(response => {
                console.log('Backend response status:', response.status);
                return response.json().then(data => ({
                    ok: response.ok,
                    status: response.status,
                    data: data
                }));
            })
            .then(({ ok, status, data }) => {
                console.log('Backend response data:', data);
                
                if (ok && data.status) {
                    // Success handling
                    setStatusMessage("Successfully connected to Threads");
                    setIsAlertOpen(true);
                    setStatusType('success');

                    // Redirect after short delay to ensure user sees success message
                    setTimeout(() => {
                        navigate('/profile');
                    }, 2000);
                } else {
                    // Handle different error types
                    let errorMessage = data.error || data.message || 'Failed to connect Threads account';
                    
                    // Log debug information if provided by backend
                    if (data.debug_info) {
                        console.log('Backend debug info:', data.debug_info);
                    }
                    
                    // Provide user-friendly error messages
                    if (errorMessage.includes('Invalid state token')) {
                        errorMessage = 'Security validation failed. Please try connecting again.';
                    } else if (errorMessage.includes('Missing required parameters')) {
                        errorMessage = 'Authorization parameters missing. Please try connecting again.';
                    } else if (errorMessage.includes('Configuration error')) {
                        errorMessage = 'Service configuration issue. Please contact support.';
                    }
                    
                    throw new Error(errorMessage);
                }
            })
            .catch((error) => {
                console.error('Threads callback processing error:', error);
                logError('Threads callback processing error', {
                    error: error.message,
                    url: location.search,
                    code: !!code,
                    state: !!state
                });
                
                setStatusMessage(error.message || 'Failed to connect Threads account. Please try again.');
                setIsAlertOpen(true);
                setStatusType('error');
                navigate('/profile');
            });
    }, [location, navigate, setIsAlertOpen, setStatusMessage, setStatusType]);

    return (
        <div role="main" aria-live="polite">
            <Typography variant="h1" component="h1" gutterBottom>
                Connecting to Threads
            </Typography>
            <Typography variant="h4" component="p" gutterBottom>
                Please wait while we complete your connection...
            </Typography>
            {/* Add loading indicator for better UX */}
            <div style={{ marginTop: '20px' }}>
                <Typography variant="body1" color="textSecondary">
                    Verifying authorization and setting up your account...
                </Typography>
            </div>
        </div>
    );
}

// Add prop types for better development experience
SocialConnectionThreads.propTypes = {};

export default SocialConnectionThreads;